import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import CustomModal from "../../../base-components/CustomModal";
import Popup from "../../../base-components/Popup";
import TablePaginationActions from "../../../base-components/TablePaginationActions";
import { getSessionUser, isPlatformSuperAdmin } from "../../../utils/roleAccess";
import { PageHeader } from "../../../ui";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const FEATURE_LABELS = {
  dashboard: "Dashboard",
  locations: "Locations",
  members: "Members",
  subscriptions: "Subscriptions",
  facilities: "Facilities",
  packages: "Packages",
  company_ledgers: "Company Ledgers",
  expense: "Expense",
  pos: "Point of Sale",
  trainers: "Trainers",
  assets: "Assets",
  staff_members: "Staff Members",
  staff_attendance: "Staff Attendance",
  visitors: "Visitors",
  reporting: "Reporting",
  settings: "Settings",
};

function Companies() {
  const sessionUser = useMemo(() => getSessionUser(), []);
  const actingUserId = sessionUser?._id;
  const isSuperAdmin = isPlatformSuperAdmin(sessionUser?.role);

  const [companies, setCompanies] = useState([]);
  const [featureCatalog, setFeatureCatalog] = useState(Object.keys(FEATURE_LABELS));
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    company_name: "",
    company_email: "",
    password: "",
    phone_no: "",
    address: "",
    admin_name: "",
  });
  const [selectedFeatures, setSelectedFeatures] = useState([]);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const showPopup = (success, message) => {
    setIsSuccessAlert(success);
    setPopupMessage(message);
    setIsPopupOpen(true);
    setTimeout(() => setIsPopupOpen(false), 4000);
  };

  const loadCompanies = useCallback(async () => {
    if (!actingUserId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ actingUserId });
      if (query.trim()) params.set("q", query.trim());
      const res = await fetch(`${BASE_URL}super-admin/companies?${params}`);
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to load companies");
        return;
      }
      setCompanies(data.companies || []);
      if (Array.isArray(data.featureCatalog) && data.featureCatalog.length) {
        setFeatureCatalog(data.featureCatalog);
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to load companies");
    } finally {
      setLoading(false);
    }
  }, [actingUserId, query]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const resetForm = () => {
    setForm({
      company_name: "",
      company_email: "",
      password: "",
      phone_no: "",
      address: "",
      admin_name: "",
    });
    setSelected(null);
    setSelectedFeatures([]);
  };

  const toggleFeature = (key) => {
    setSelectedFeatures((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const openCreate = () => {
    resetForm();
    setSelectedFeatures([...featureCatalog]);
    setIsCreateOpen(true);
  };

  const openEdit = (company) => {
    setSelected(company);
    setForm({
      company_name: company.company_name || "",
      company_email: company.company_email || "",
      password: "",
      phone_no: company.phone_no || "",
      address: company.address || "",
      admin_name: "",
    });
    setIsEditOpen(true);
  };

  const openFeatures = (company) => {
    setSelected(company);
    setSelectedFeatures(
      Array.isArray(company.enabled_features) && company.enabled_features.length
        ? [...company.enabled_features]
        : [...featureCatalog]
    );
    setIsFeaturesOpen(true);
  };

  const openDelete = (company) => {
    setSelected(company);
    setIsDeleteOpen(true);
  };

  const handleCreate = async () => {
    if (!actingUserId || !form.company_name.trim() || !form.company_email.trim() || !form.password) {
      showPopup(false, "Name, email, and password are required");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}super-admin/companies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          actingUserId,
          company_name: form.company_name.trim(),
          company_email: form.company_email.trim(),
          password: form.password,
          phone_no: form.phone_no,
          address: form.address,
          admin_name: form.admin_name,
          enabled_features: selectedFeatures,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to create company");
      } else {
        showPopup(true, "Company created successfully");
        setIsCreateOpen(false);
        resetForm();
        loadCompanies();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to create company");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!actingUserId || !selected) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}super-admin/companies/${selected._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          actingUserId,
          company_name: form.company_name.trim(),
          company_email: form.company_email.trim(),
          phone_no: form.phone_no,
          address: form.address,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to update company");
      } else {
        showPopup(true, "Company updated successfully");
        setIsEditOpen(false);
        resetForm();
        loadCompanies();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to update company");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeatures = async () => {
    if (!actingUserId || !selected) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${BASE_URL}super-admin/companies/${selected._id}/features`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            actingUserId,
            enabled_features: selectedFeatures,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to update features");
      } else {
        showPopup(true, "Features updated successfully");
        setIsFeaturesOpen(false);
        resetForm();
        loadCompanies();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to update features");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!actingUserId || !selected) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${BASE_URL}super-admin/companies/${selected._id}?actingUserId=${actingUserId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ actingUserId }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to delete company");
      } else {
        showPopup(true, "Company deleted successfully");
        setIsDeleteOpen(false);
        resetForm();
        loadCompanies();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to delete company");
    } finally {
      setSaving(false);
    }
  };

  const paginated =
    rowsPerPage > 0
      ? companies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : companies;

  if (!isSuperAdmin) {
    return (
      <div className="m-5 bg-gx-border p-5 rounded-lg">
        <h3 className="text-xl font-bold text-center text-gx-ink-soft">
          Super Admin access required
        </h3>
      </div>
    );
  }

  const FeatureCheckboxes = () => (
    <FormGroup className="mt-3 max-h-64 overflow-y-auto border rounded p-2">
      {featureCatalog.map((key) => (
        <FormControlLabel
          key={key}
          control={
            <Checkbox
              checked={selectedFeatures.includes(key)}
              onChange={() => toggleFeature(key)}
            />
          }
          label={FEATURE_LABELS[key] || key}
        />
      ))}
    </FormGroup>
  );

  return (
    <>
      <div className="m-5">
        <PageHeader
          title="Companies"
          subtitle="Registered gyms, locations, members, and enabled features"
          actions={
            <button className="gx-cta" onClick={openCreate}>
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Company
            </button>
          }
        />
      </div>

      <div className="m-5 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm text-gx-ink-soft">Search</label>
          <input
            type="text"
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0"
            placeholder="Search by name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadCompanies()}
          />
        </div>
        <button className="gx-cta" onClick={loadCompanies} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      {companies.length > 0 ? (
        <Box className="bg-gx-surface rounded-gx border border-gx-border shadow-gx m-5">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Locations</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Features</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((company) => (
                <TableRow hover key={company._id}>
                  <TableCell>{company.company_name}</TableCell>
                  <TableCell>{company.company_email}</TableCell>
                  <TableCell>{company.locations_count ?? 0}</TableCell>
                  <TableCell>{company.members_count ?? 0}</TableCell>
                  <TableCell>
                    {(company.enabled_features || []).includes("pos")
                      ? `${(company.enabled_features || []).length} enabled`
                      : `${(company.enabled_features || []).length} enabled (POS off)`}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-3 flex-wrap">
                      <button
                        type="button"
                        className="text-gx-ink font-bold hover:text-gx-red"
                        onClick={() => openEdit(company)}
                      >
                        <FontAwesomeIcon icon={faPen} /> Edit
                      </button>
                      <button
                        type="button"
                        className="text-gx-ink font-bold hover:text-gx-red"
                        onClick={() => openFeatures(company)}
                      >
                        Features
                      </button>
                      <button
                        type="button"
                        className="text-gx-ink font-bold hover:text-red-700"
                        onClick={() => openDelete(company)}
                      >
                        <FontAwesomeIcon icon={faTrash} /> Delete
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  colSpan={6}
                  count={companies.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      ) : (
        <div className="bg-gx-border w-full p-3 mt-5 rounded-lg m-5">
          <h3 className="text-xl font-bold text-center text-gx-ink-soft">
            {loading ? "Loading..." : "No companies found"}
          </h3>
        </div>
      )}

      <CustomModal isModal={isCreateOpen} handleModal={() => setIsCreateOpen(false)} size="2xl">
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">Add Company</h1>
        <div className="m-5 text-gx-ink-soft">
          <label>Company name</label>
          <input
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          />
          <label>Company email</label>
          <input
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.company_email}
            onChange={(e) => setForm({ ...form, company_email: e.target.value })}
          />
          <label>Admin name</label>
          <input
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.admin_name}
            onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
          />
          <label>Admin password</label>
          <input
            type="password"
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <label>Phone</label>
          <input
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.phone_no}
            onChange={(e) => setForm({ ...form, phone_no: e.target.value })}
          />
          <label>Address</label>
          <input
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <div className="mt-3 font-medium">Enabled features</div>
          <FeatureCheckboxes />
          <div className="flex justify-end pt-3 mt-3 border-t">
            <button className="gx-cta" onClick={handleCreate} disabled={saving}>
              {saving ? "Saving..." : "Create"}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal isModal={isEditOpen} handleModal={() => setIsEditOpen(false)} size="2xl">
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">Edit Company</h1>
        <div className="m-5 text-gx-ink-soft">
          <label>Company name</label>
          <input
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          />
          <label>Company email</label>
          <input
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.company_email}
            onChange={(e) => setForm({ ...form, company_email: e.target.value })}
          />
          <label>Phone</label>
          <input
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.phone_no}
            onChange={(e) => setForm({ ...form, phone_no: e.target.value })}
          />
          <label>Address</label>
          <input
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <div className="flex justify-end pt-3 mt-3 border-t">
            <button className="gx-cta" onClick={handleUpdate} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal isModal={isFeaturesOpen} handleModal={() => setIsFeaturesOpen(false)} size="2xl">
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">
          Features — {selected?.company_name}
        </h1>
        <div className="m-5 text-gx-ink-soft">
          <p className="text-sm mb-2">
            Uncheck a module to hide it from every user in this company. For example,
            turning off Point of Sale removes POS from their menu and APIs.
          </p>
          <FeatureCheckboxes />
          <div className="flex justify-end pt-3 mt-3 border-t">
            <button className="gx-cta" onClick={handleSaveFeatures} disabled={saving}>
              {saving ? "Saving..." : "Save features"}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal isModal={isDeleteOpen} handleModal={() => setIsDeleteOpen(false)} size="md">
        <div className="m-5">
          <h2 className="text-xl font-medium">Delete Company?</h2>
          <p className="text-gx-ink-soft mt-3">
            This will deactivate &quot;{selected?.company_name}&quot; and its login users.
            Records are kept (soft delete).
          </p>
          <button
            className="bg-red-600 p-2 px-5 rounded-lg text-white font-medium hover:bg-red-700 mt-5 float-right mb-5"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? "Deleting..." : "Delete"}
          </button>
        </div>
      </CustomModal>

      {isPopupOpen && (
        <Popup
          onClose={() => setIsPopupOpen(false)}
          alertType={isSuccessAlert ? "success" : "failure"}
          message={popupMessage}
        />
      )}
    </>
  );
}

export default Companies;
